import * as XLSX from "xlsx";

import type { ClaseType, ReporteType } from "~/types/contabilidad";
import { CLASE_OPTIONS, REPORTE_OPTIONS } from "~/types/contabilidad";

export interface ParsedMappingLine {
  rowNumber: number;
  account_code: string;
  reporte: ReporteType;
  clase: ClaseType;
  orden_clase: number;
  orden_tipo: number;
  tipo?: string;
  sub_clase: string;
  orden_subclase: number;
  sub_clase_2: string;
  orden_subclase_2: number;
  grupo_cuenta_code?: string;
  orden_grupo_cuenta: number;
  clave?: number;
  signo?: number;
  clase_1?: string;
  error?: string;
  existsInDb?: boolean;
  accountExists?: boolean;
}

export interface MappingParseResult {
  lines: ParsedMappingLine[];
  errors: string[];
  summary: {
    toCreate: number;
    toUpdate: number;
    invalid: number;
  };
}

// Column name mapping (supports various formats)
const COLUMN_MAP: Record<string, string> = {
  // Account code
  codigo: "account_code",
  código: "account_code",
  code: "account_code",
  codigo_cuenta: "account_code",
  "codigo cuenta": "account_code",

  // Reporte
  reporte: "reporte",
  report: "reporte",

  // Clase
  clase: "clase",
  class: "clase",

  // Orden clase
  orden_clase: "orden_clase",
  "orden clase": "orden_clase",
  ordenclase: "orden_clase",

  // Orden tipo
  orden_tipo: "orden_tipo",
  "orden tipo": "orden_tipo",
  ordentipo: "orden_tipo",

  // Tipo
  tipo: "tipo",
  type: "tipo",

  // Sub clase
  sub_clase: "sub_clase",
  subclase: "sub_clase",
  "sub clase": "sub_clase",

  // Orden subclase
  orden_subclase: "orden_subclase",
  "orden subclase": "orden_subclase",
  ordensubclase: "orden_subclase",

  // Sub clase 2
  sub_clase_2: "sub_clase_2",
  subclase_2: "sub_clase_2",
  subclase2: "sub_clase_2",
  "sub clase 2": "sub_clase_2",
  "subclase 2": "sub_clase_2",

  // Orden subclase 2
  orden_subclase_2: "orden_subclase_2",
  "orden subclase 2": "orden_subclase_2",
  ordensubclase2: "orden_subclase_2",

  // Grupo cuenta
  grupo_cuenta: "grupo_cuenta_code",
  grupocuenta: "grupo_cuenta_code",
  "grupo cuenta": "grupo_cuenta_code",

  // Orden grupo cuenta
  orden_grupo_cuenta: "orden_grupo_cuenta",
  "orden grupo cuenta": "orden_grupo_cuenta",
  "orden grupo cta": "orden_grupo_cuenta",
  "orden grupo cta.": "orden_grupo_cuenta",
  ordengrupoct: "orden_grupo_cuenta",
  ordengrupocta: "orden_grupo_cuenta",

  // Clave
  clave: "clave",
  key: "clave",

  // Signo
  signo: "signo",
  sign: "signo",

  // Clase 1
  clase_1: "clase_1",
  clase1: "clase_1",
  "clase 1": "clase_1",
};

// Reporte type normalization
const REPORTE_MAP: Record<string, ReporteType> = {
  balance: "Balance",
  resultados: "Resultados",
  resultado: "Resultados",
};

// Clase type normalization
const CLASE_MAP: Record<string, ClaseType> = {
  activo: "Activo",
  "pasivo y patrimonio": "Pasivo y Patrimonio",
  pasivoypatrimonio: "Pasivo y Patrimonio",
  costos: "Costos",
  costo: "Costos",
  gastos: "Gastos",
  gasto: "Gastos",
  ingresos: "Ingresos",
  ingreso: "Ingresos",
};

/**
 * Parse number values, returning undefined for empty/invalid
 */
function parseNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const num = Number(value);
  return isNaN(num) ? undefined : num;
}

/**
 * Normalize reporte string to valid ReporteType
 */
function normalizeReporte(value: unknown): ReporteType | null {
  if (!value) return null;

  const str = String(value).toLowerCase().trim();
  return REPORTE_MAP[str] || null;
}

/**
 * Normalize clase string to valid ClaseType
 */
function normalizeClase(value: unknown): ClaseType | null {
  if (!value) return null;

  const str = String(value).toLowerCase().trim();
  return CLASE_MAP[str] || null;
}

/**
 * Parse Excel/CSV file and extract mapping data
 */
export async function parseMappingFile(
  file: File,
  existingAccountCodes: Set<string>,
  existingMappingAccountCodes: Set<string>
): Promise<MappingParseResult> {
  const errors: string[] = [];
  const lines: ParsedMappingLine[] = [];

  try {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });

    // Try to find "Mapeo" or "Mapping" sheet, otherwise use first sheet
    let sheetName = workbook.SheetNames.find(
      (name) =>
        name.toLowerCase() === "mapeo" ||
        name.toLowerCase() === "mapping" ||
        name.toLowerCase() === "cuentas"
    );
    if (!sheetName) {
      sheetName = workbook.SheetNames[0];
    }

    if (!sheetName) {
      errors.push("El archivo no contiene hojas de cálculo");
      return {
        lines,
        errors,
        summary: { toCreate: 0, toUpdate: 0, invalid: 0 },
      };
    }

    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      errors.push("No se pudo leer la hoja de cálculo");
      return {
        lines,
        errors,
        summary: { toCreate: 0, toUpdate: 0, invalid: 0 },
      };
    }

    // Convert to JSON (array of arrays)
    const data = XLSX.utils.sheet_to_json<(string | number | null)[]>(sheet, {
      header: 1,
      defval: "",
    });

    if (data.length < 2) {
      errors.push(
        "El archivo debe tener al menos una fila de encabezado y una fila de datos"
      );
      return {
        lines,
        errors,
        summary: { toCreate: 0, toUpdate: 0, invalid: 0 },
      };
    }

    // Parse header row to determine column indices
    const headerRow = data[0];
    if (!headerRow) {
      errors.push("No se encontró la fila de encabezado");
      return {
        lines,
        errors,
        summary: { toCreate: 0, toUpdate: 0, invalid: 0 },
      };
    }

    const columnIndices: Record<string, number> = {};
    headerRow.forEach((header, index) => {
      const headerStr = String(header || "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, " ");
      const mappedField = COLUMN_MAP[headerStr];
      if (mappedField) {
        columnIndices[mappedField] = index;
      }
    });

    // Verify required columns exist
    if (columnIndices.account_code === undefined) {
      errors.push("Columna 'Codigo' o 'Cuenta' no encontrada en el archivo");
    }
    if (columnIndices.reporte === undefined) {
      errors.push("Columna 'Reporte' no encontrada en el archivo");
    }
    if (columnIndices.clase === undefined) {
      errors.push("Columna 'Clase' no encontrada en el archivo");
    }
    if (columnIndices.sub_clase === undefined) {
      errors.push("Columna 'Sub Clase' no encontrada en el archivo");
    }
    if (columnIndices.sub_clase_2 === undefined) {
      errors.push("Columna 'SubClase 2' no encontrada en el archivo");
    }

    if (errors.length > 0) {
      return {
        lines,
        errors,
        summary: { toCreate: 0, toUpdate: 0, invalid: 0 },
      };
    }

    // Track codes within this file to detect duplicates
    const codesInFile = new Map<string, number>();

    // Process data rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.length === 0) continue;

      const accountCode = String(row[columnIndices.account_code] || "").trim();

      // Skip completely empty rows
      if (!accountCode) continue;

      const rawReporte = row[columnIndices.reporte];
      const reporte = normalizeReporte(rawReporte);

      const rawClase = row[columnIndices.clase];
      const clase = normalizeClase(rawClase);

      const subClase = String(row[columnIndices.sub_clase] ?? "").trim();
      const subClase2 = String(row[columnIndices.sub_clase_2] ?? "").trim();

      const parsedLine: ParsedMappingLine = {
        rowNumber: i + 1, // 1-indexed for user display
        account_code: accountCode,
        reporte: reporte || "Balance",
        clase: clase || "Activo",
        orden_clase:
          parseNumber(row[columnIndices.orden_clase]) ?? 0,
        orden_tipo:
          parseNumber(row[columnIndices.orden_tipo]) ?? 0,
        tipo:
          columnIndices.tipo !== undefined
            ? String(row[columnIndices.tipo] ?? "").trim() || undefined
            : undefined,
        sub_clase: subClase,
        orden_subclase:
          parseNumber(row[columnIndices.orden_subclase]) ?? 0,
        sub_clase_2: subClase2,
        orden_subclase_2:
          parseNumber(row[columnIndices.orden_subclase_2]) ?? 0,
        grupo_cuenta_code:
          columnIndices.grupo_cuenta_code !== undefined
            ? String(row[columnIndices.grupo_cuenta_code] ?? "").trim() ||
              undefined
            : undefined,
        orden_grupo_cuenta:
          parseNumber(row[columnIndices.orden_grupo_cuenta]) ?? 0,
        clave:
          columnIndices.clave !== undefined
            ? parseNumber(row[columnIndices.clave])
            : undefined,
        signo:
          columnIndices.signo !== undefined
            ? parseNumber(row[columnIndices.signo])
            : undefined,
        clase_1:
          columnIndices.clase_1 !== undefined
            ? String(row[columnIndices.clase_1] ?? "").trim() || undefined
            : undefined,
      };

      // Validate the line
      const lineErrors: string[] = [];

      // Check for empty required fields
      if (!accountCode) {
        lineErrors.push("Codigo cuenta es requerido");
      }

      // Check if account exists in the company
      const codeLower = accountCode.toLowerCase();
      if (!existingAccountCodes.has(codeLower)) {
        lineErrors.push("Cuenta no existe en la empresa");
        parsedLine.accountExists = false;
      } else {
        parsedLine.accountExists = true;
      }

      // Validate reporte
      if (!reporte) {
        lineErrors.push(
          `Reporte '${rawReporte}' no válido. Use: ${REPORTE_OPTIONS.join(", ")}`
        );
      }

      // Validate clase
      if (!clase) {
        lineErrors.push(
          `Clase '${rawClase}' no válida. Use: ${CLASE_OPTIONS.join(", ")}`
        );
      }

      // Validate sub_clase
      if (!subClase) {
        lineErrors.push("Sub Clase es requerido");
      }

      // Validate sub_clase_2
      if (!subClase2) {
        lineErrors.push("SubClase 2 es requerido");
      }

      // Check for duplicate code within the file
      if (codesInFile.has(codeLower)) {
        lineErrors.push(
          `Codigo duplicado (ver fila ${codesInFile.get(codeLower)})`
        );
      } else {
        codesInFile.set(codeLower, i + 1);
      }

      // Check if mapping already exists in template
      if (existingMappingAccountCodes.has(codeLower)) {
        parsedLine.existsInDb = true;
      }

      if (lineErrors.length > 0) {
        parsedLine.error = lineErrors.join(". ");
      }

      lines.push(parsedLine);
    }

    if (lines.length === 0) {
      errors.push("No se encontraron líneas de datos válidas en el archivo");
    }
  } catch (error) {
    errors.push(
      `Error al leer el archivo: ${error instanceof Error ? error.message : "Error desconocido"}`
    );
  }

  // Calculate summary
  const toCreate = lines.filter((l) => !l.error && !l.existsInDb).length;
  const toUpdate = lines.filter((l) => !l.error && l.existsInDb).length;
  const invalid = lines.filter((l) => l.error).length;

  return {
    lines,
    errors,
    summary: {
      toCreate,
      toUpdate,
      invalid,
    },
  };
}

/**
 * Check if there are any validation errors
 */
export function hasMappingValidationErrors(result: MappingParseResult): boolean {
  return result.errors.length > 0;
}

/**
 * Check if there are any lines that can be imported
 */
export function hasImportableMappings(result: MappingParseResult): boolean {
  return result.summary.toCreate > 0 || result.summary.toUpdate > 0;
}

/**
 * Get valid lines for import (no errors)
 */
export function getValidMappingLines(
  result: MappingParseResult
): ParsedMappingLine[] {
  return result.lines.filter((line) => !line.error);
}
